/**
 * Typed wrappers around the /api/study-guides endpoints.
 *
 * Backend reference: ``backend/caitapi/routers/study_guides.py``. Types
 * mirror the Pydantic response models 1:1 — keep in sync when those
 * change.
 *
 * Auth: every endpoint requires an authenticated session (401) plus an
 * active enrollment (403). Callers handle 401 by redirecting through
 * ``/api/auth/login?return_to=...`` (same pattern as ProgressPage).
 *
 * Per DEC-0033 (data model), DEC-0034 (>= 0.90 completion), DEC-0035
 * (6h presigned URL + save cadence), DEC-0036 (10 XP on completion).
 */
import { API_BASE_URL, apiGet, apiPut } from "./api";

// ---------------------------------------------------------------------------
// Read: study guide metadata + presigned URLs
// ---------------------------------------------------------------------------

export interface StudyGuide {
  id: string;
  module_id: string;
  title: string;
  duration_seconds: number;
  content_version: string;
  /** 6-hour presigned GET URL (DEC-0035). Supports HTTP range requests. */
  audio_url: string;
  /** Presigned GET URL for the transcript text, when present. */
  transcript_url: string | null;
}

/** Fetch the audio Study Guide for a given module. 404 when none exists. */
export function getByModule(moduleId: string): Promise<StudyGuide> {
  return apiGet<StudyGuide>(`/api/study-guides/by-module/${moduleId}`);
}

// ---------------------------------------------------------------------------
// Read: caller's resume + completion state
// ---------------------------------------------------------------------------

export interface StudyGuideProgress {
  study_guide_id: string;
  /** Where to resume on next load (DEC-0033). */
  position_seconds: number;
  /** Monotone high-water mark; never decreases. Drives 90% completion (DEC-0034). */
  furthest_seconds: number;
  duration_seconds: number;
  completed: boolean;
  /** ISO 8601 timestamp on the false→true transition. Stamped once. */
  completed_at: string | null;
  play_count: number;
  /** ISO 8601 timestamp of the last save. */
  last_played_at: string | null;
}

export function getProgress(studyGuideId: string): Promise<StudyGuideProgress> {
  return apiGet<StudyGuideProgress>(
    `/api/study-guides/${studyGuideId}/progress`,
  );
}

// ---------------------------------------------------------------------------
// Write: PUT (interactive saves) + sendBeacon (unload saves)
// ---------------------------------------------------------------------------

export interface StudyGuideProgressUpdate {
  position_seconds: number;
  /**
   * Optional client-reported high-water mark. Server clamps with
   * GREATEST(stored, submitted) so a backward scrub cannot decrease the
   * persisted value (DEC-0034).
   */
  furthest_seconds?: number;
}

export function putProgress(
  studyGuideId: string,
  body: StudyGuideProgressUpdate,
): Promise<StudyGuideProgress> {
  return apiPut<StudyGuideProgress>(
    `/api/study-guides/${studyGuideId}/progress`,
    body,
  );
}

/**
 * Fire-and-forget save via ``navigator.sendBeacon`` for ``beforeunload`` and
 * ``pagehide`` events — per DEC-0035. ``sendBeacon`` is POST-only and uses
 * Content-Type: application/json (via a Blob) so FastAPI parses the body.
 *
 * Returns true when the beacon was queued successfully. False means the
 * browser refused (typically because the payload would exceed the per-
 * origin sendBeacon quota or the document is already unloaded).
 */
export function beaconProgress(
  studyGuideId: string,
  body: StudyGuideProgressUpdate,
): boolean {
  if (typeof navigator === "undefined" || !navigator.sendBeacon) {
    return false;
  }
  const url = `${API_BASE_URL}/api/study-guides/${studyGuideId}/progress/beacon`;
  const blob = new Blob([JSON.stringify(body)], {
    type: "application/json",
  });
  return navigator.sendBeacon(url, blob);
}
